import { Component, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { AlertController, MenuController, ModalController, NavController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { Store } from '@ngrx/store';
import { Config, General, GlobalErrorResponse, OrganizationCode } from 'merchandising-sdk';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MenuProp } from './stores/menu/menu.prop';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { MiscHelper } from './helpers/misc';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import Crashes from '@capacitor-community/appcenter-crashes';
import Analytics from '@capacitor-community/appcenter-analytics';
import { LocalNotifications } from '@capacitor/local-notifications'
import { UserProp } from './stores/user/user.prop';
import { setLogin } from './stores/user/user.actions';
import { setDataUpdates, setSurveyCount } from './stores/menu/menu.actions';
import { version } from 'src/version';
import * as moment from 'moment';
import { NavigationEnd, Router } from '@angular/router';
//import OneSignal from 'onesignal-cordova-plugin';
import { NotificationService } from './services/notification.service';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
})
export class AppComponent implements OnInit {
  constructor(private nav: NavController
    , private storage: Storage
    , private menu: MenuController
    , private alert: AlertController
    , private router: Router
    , private notificationService: NotificationService
    , private store: Store<{ menu: MenuProp, user: UserProp }>) { }

  public has_data_updates: boolean;
  public $menu: Observable<MenuProp>;
  public $user: Observable<UserProp>;
  public orders_count: number = 0;
  public surveys_count: number = 0;
  public is_data_updates_enabled: boolean;
  public has_manage_order: boolean = false;
  public has_manage_users: boolean = false;
  public role: string = '';
  public organization: string = environment.organization;

  public settings: any = {
    logo: "",
    has_orders: true,
    branding: "",
    footer: {
      has_copyright: true
    }
  };
  public is_menu_enabled: boolean;
  public version: string;

  ngOnInit() {
    //  initialize config
    Config.IS_ONLINE = environment.is_online;
    if (!environment.is_online) {
      this.is_data_updates_enabled = true;
    }

    //  initialize store
    this.$menu = this.store.select("menu");
    this.$user = this.store.select("user");

    //  subscribe to user
    this.$user.subscribe(async data => {
      const { is_login } = data;
      this.is_menu_enabled = is_login;
      if (is_login) {
        this.store.dispatch(setDataUpdates({ has_data_updates: false }));
        this.checkRole();
        await this.setOrganizationSettings();
      }
    });

    this.$menu.subscribe(async data => {
      this.has_data_updates = data.has_data_updates;
      if (!environment.is_online) {
        await this.setLocalAssetUrl();
      }
      if (data.has_data_updates) {
        await this.setOrganizationSettings();
      }

      this.orders_count = data.orders_count;
      this.surveys_count = data.surveys_count;
      console.log('AppComponent menu subscription - surveys_count:', this.surveys_count);
    });

    this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        this.saveVisitedUrl(e.url)
      }
    })

    this.init();
  }

  async saveVisitedUrl(url: string) {
    const storage = await this.storage.create()
    let visited_urls: Array<any> = await storage.get(`visited_urls`)
    if (!visited_urls) {
      visited_urls = []
    }
    let is_add = true;
    if (visited_urls.length > 0) {
      const last_visited_url = visited_urls[visited_urls.length - 1]
      if (last_visited_url.url === url) {
        is_add = false;
      }
    }
    if (is_add) {
      visited_urls.push({
        url: url,
        visited_at: new Date().getTime()
      })
      await storage.set(`visited_urls`, visited_urls)
    }
    await this.ping();
  }

  async ping() {
    const storage = await this.storage.create()
    const last_ping = await storage.get(`last_ping`)
    const visited_urls: Array<any> = await storage.get(`visited_urls`)
    let to_ping = false;
    if (last_ping) {
      const { date } = last_ping
      const treshold_date = moment(new Date()).subtract(`15`, 'minutes')
      if (moment(date).isBefore(treshold_date)) {
        to_ping = true
      }
    } else {
      to_ping = true;
    }

    const is_login = await storage.get(`is_login`)
    if (to_ping && is_login) {
      const orders = await storage.get(`orders`)
      const surveys = await storage.get(`surveys`)
      const username = await storage.get(`username`)
      const additional_data: any = {}
      if (visited_urls) {
        additional_data.visited_urls = visited_urls
      }
      General.Ping(username, environment.organization, (orders && orders.length > 0) ? 1 : 0, JSON.stringify(additional_data), (surveys && surveys.length > 0) ? 1 : 0)
      await storage.set(`last_ping`, {
        date: new Date(),
        has_unsync_orders: (orders && orders.length > 0)
      })
      await storage.set(`visited_urls`, [])
    }
    console.log(`to_ping`, to_ping)
  }

  async init() {
    this.ping()
    if (!environment.is_online) {
      try {
        await Crashes.setEnabled({ enable: true });
        await Analytics.setEnabled({ enable: false });
        await Analytics.enableManualSessionTracker();
        await Analytics.startSession();
        console.log(`tracking started`);
      } catch (e) {
        console.log(`tracking failed`)
      }

      try {
        //await this.initializeNotification();
        console.log(`notification initialized`)
      } catch (e) {
        console.log(`notification failed`)
      }
    }

    App.addListener('appStateChange', change => {
      const { isActive: is_active } = change
      if (is_active) {
        this.ping()
      }
    });

    let storage = await this.storage.create();
    if (await storage.get('is_login')) {
      this.store.dispatch(setLogin({ is_login: true }));
    }
    await this.checkRole();
    await this.setOrganizationSettings();

    const surveys = await storage.get('surveys');
    console.log('AppComponent init - surveys from storage:', surveys);
    if (surveys && surveys.length > 0) {
      console.log('AppComponent init - dispatching setSurveyCount:', surveys.length);
      this.store.dispatch(setSurveyCount({ surveys_count: surveys.length }));
    } else {
      console.log('AppComponent init - no surveys or empty array');
    }

    //  set app version
    let app_info = null;
    try {
      app_info = await App.getInfo();
      this.version = app_info.version;
    } catch (e) {
      this.version = version.name
    }

    //  check if version is valid for mobile
    this.CheckAppUpdates();

    if (!environment.is_online) {
      const existing_schedules = await LocalNotifications.getPending()
      console.log(`existing_schedules`, existing_schedules)
      const schedules = [moment(), moment().add(1, 'day')]
      for (let index in schedules) {
        const item = schedules[index]
        let hour = 23
        let id = parseInt(item.format("YYYYMMDD") + "23")
        if (parseInt(index) === 1) {
          hour = 6
          id = parseInt(item.format("YYYYMMDD") + "06")
        }
        const schedule = item.set({ hour: hour, minutes: 0, seconds: 0 }).toDate();
        await LocalNotifications.schedule({
          notifications: [
            {
              title: `Sync Orders`,
              body: `This is just a friendly reminder for you to sync all the orders and surveys you added for ${hour === 23 && 'today' || 'yesterday'}'s store visits`,
              id: id,
              schedule: {
                at: schedule,
                allowWhileIdle: true
              }
            }
          ]
        })
      }
    }
  }

  async CheckAppUpdates() {
    if (!environment.is_online) {
      try {
        const result = await General.CheckStatus();
        const { error } = result as GlobalErrorResponse;
        if (!error) {
          const { version, status } = result as any;
          const release_version = parseInt(version.replace(/\./g, ''));
          const app_version = parseInt(this.version.replace(/\./g, ''));
          if (app_version < release_version) {
            const { platform } = await Device.getInfo();
            if (platform === 'ios') {
              this.showIosUpdate(version);
            }

            if (platform === 'android') {
              this.showAndroidUpdate(version);
            }
          }
        }
      } catch (e) {
        console.log(`Can't access api for now`, e);
      }
    }
  }

  async showIosUpdate(version: string) {
    let app_name = `LOP Merchandising`;
    if (environment.organization == OrganizationCode.GMI) {
      app_name = `GMI Merchandising`;
    }
    const alert = await this.alert.create({
      header: `Version ${version} is available`,
      message: `Sorry to interupt you, 
                There is an important app update we require you 
                to install before you continue using the app. 
                Go to App Store > Profile Picture Icon (Top Right) 
                > Purchased > Find ${app_name} and click update`,
      buttons: [
        {
          text: `Exit`,
          handler: async () => {
            App.exitApp();
          }
        }
      ],
      backdropDismiss: false
    });
    alert.present();
  }

  async showAndroidUpdate(version: string) {
    const alert = await this.alert.create({
      header: `Version ${version} is available`,
      message: `Sorry to interupt you, There is an important app update we require you to install before you continue using the app.`,
      buttons: [
        {
          text: `Update`,
          handler: () => {
            this.redirectToAndroidStore();
            return false;
          }
        }
      ],
      backdropDismiss: false
    });
    alert.present();
  }

  async redirectToAndroidStore() {
    const env = environment as any;
    let url = '';
    if (environment.organization == OrganizationCode.LOP) {
      url = `https://play.google.com/store/apps/details?id=app.merchandising.lop`;
      if (env.is_apk) {
        url = `https://lop.merchandising.app/download`;
      }
    }
    if (environment.organization == OrganizationCode.GMI) {
      url = `https://play.google.com/store/apps/details?id=app.merchandising.gmi`;
      if (env.is_apk) {
        url = `https://gmi.merchandising.app/download`;
      }
    }
    window.open(url);
  }

  async checkRole() {
    let storage = await this.storage.create();
    const role = await storage.get(`role`);
    this.role = role;
    if (environment.is_online && ['match_admin', 'client_admin', 'admin'].indexOf(role) > -1) {
      this.has_manage_users = true;
    } else {
      this.has_manage_users = false;
    }

    if (environment.is_online && ['match_admin', 'client_admin', 'admin', 'manager', 'sdm_supervisor', 'sdm_supervisor', 'supervisor'].indexOf(role) > -1) {
      this.has_manage_order = true;
    } else {
      this.has_manage_order = false;
    }
  }

  async setOrganizationSettings() {
    const settings = await MiscHelper.getJson('/app/settings.json');
    if (settings) {
      if (settings.menu) {
        if (settings.menu.logo) {
          settings.menu.logo = await MiscHelper.getAssetUrl(`/app/${settings.menu.logo}`)
        }
        if (settings.menu.branding) {
          settings.menu.branding = await MiscHelper.getAssetUrl(`/app/${settings.menu.branding}`);
        }
      }
      let { menu } = settings;
      this.settings = menu;
    }
  }

  async setLocalAssetUrl() {
    let result = await Filesystem.getUri({
      path: 'repo',
      directory: Directory.Data
    });
    environment.assets_url = Capacitor.convertFileSrc(result.uri);
  }


  pushPage(url: string) {
    this.menu.close();
    this.nav.navigateForward(url);
  }

  rootPage(url: string) {
    this.menu.close();
    this.nav.navigateRoot(url);
  }

  async viewDemoVideo() {
    this.menu.close()
    let url = `https://stmerchandisingprod.z19.web.core.windows.net/${environment.organization}-merchandising-app-demo.mp4`
    if (this.role === 'sdm_viewer') {
      url = `https://stmerchandisingprod.z19.web.core.windows.net/sdm-merchandising-app-demo.mp4`
    }
    this.nav.navigateForward(`/viewers/video?url=${url}`)
  }

  async logout() {
    let storage = await this.storage.create();
    await storage.remove('is_login');
    await storage.remove(`role`);
    await this.menu.close();
    this.store.dispatch(setLogin({ is_login: false, user: null }));
    this.nav.navigateBack(`/auth/login`);
  }

  openStudioLink() {
    if (this.organization === 'gmi') {
      window.open('https://admin.gmi.merchandising.app/', '_blank');
    } else {
      window.open('https://admin.lop.merchandising.app/', '_blank');
    }
  }

  // async initializeNotification() {
  //   // Enable verbose logging for debugging (remove in production)
  //   // OneSignal.Debug.setLogLevel(6);
  //   // Initialize with your OneSignal App ID
  //   OneSignal.initialize(`d480d3e3-3a87-41ea-8973-fb511a583bd8`);
  //   // Use this method to prompt for push notifications.
  //   // We recommend removing this method after testing and instead use In-App Messages to prompt for notification permission.
  //   await OneSignal.Notifications.requestPermission(false)

  //   const onesignal_id = await OneSignal.User.getOnesignalId()
  //   const { platform } = await Device.getInfo();
  //   await this.notificationService.SaveDevice(onesignal_id, platform)

  //   const self = this
  //   OneSignal.Notifications.addEventListener('click', e => {
  //     if (e.notification.additionalData) {
  //       const { type, url } = e.notification.additionalData as any;
  //       switch (type) {
  //         case 'redirect':
  //           console.log(`Notification click detected, redirecting to ${url}`)
  //           self.nav.navigateForward(url)
  //           break;
  //       }
  //     }
  //   })
  // }
}